/**
 * Rate sync (System Settings → Tax Rate Templates → Rate sync) — wire schemas.
 *
 * Served by `apps/api/src/routes/accounting/tax-rate-sync.ts` under
 * `/api/v1/tax-rate-sync` (super admins only). One proposal pipeline for three
 * sources (migration 20261029100000_rate_sync_generalize.sql):
 *
 *   - `vatstack`        — EU VAT rates from the Vatstack API (daily);
 *   - `vt_lot`          — the Vermont local option tax tables (monthly);
 *   - `burlington_grt`  — the City of Burlington gross receipts tax (monthly).
 *
 * The tables keep their `vatstack_` prefix (`vatstack_sync_runs`,
 * `vatstack_sync_proposals`); `source` is what makes them general. Review,
 * bulk review and apply are Pattern C DEFINER RPCs (super admin +
 * `verify_backend_token()`); a manual run invokes the `sync-vatstack-rates`
 * edge function as the caller.
 */
import { z } from "zod";
import { calendarDateSchema } from "./dateWindow.js";
/** `vatstack_sync_runs_source_check`. */
export const RATE_SYNC_SOURCES = ["vatstack", "vt_lot", "burlington_grt"];
export const rateSyncSourceSchema = z.enum(RATE_SYNC_SOURCES);
/** GET /runs?source= — omitted = every source. */
export const rateSyncRunsQuerySchema = z
    .object({ source: rateSyncSourceSchema.optional() })
    .strict();
/** The two review outcomes `review_vatstack_proposal` accepts. */
export const rateSyncReviewActionSchema = z.enum(["approved", "rejected"]);
/**
 * POST /proposals/:id/review and POST /runs/:id/review — one proposal, or
 * every pending proposal of a run.
 */
export const reviewRateSyncProposalSchema = z
    .object({
    action: rateSyncReviewActionSchema,
    notes: z.string().trim().max(2000).optional(),
})
    .strict();
/**
 * POST /runs/:id/apply — `apply_vatstack_proposals(p_sync_run_id,
 * p_effective_from)`. The date applies to every approved proposal WITHOUT its
 * own `proposed_effective_from` (the RPC takes one date: COALESCE(proposal,
 * this)); omitted = the server's today (the RPC default).
 */
export const applyRateSyncProposalsSchema = z
    .object({ effective_from: calendarDateSchema.optional() })
    .strict();
/** POST /runs — start a manual sync of one source. */
export const startRateSyncSchema = z.object({ source: rateSyncSourceSchema }).strict();
/** `apply_vatstack_proposals`' jsonb result. */
export const applyRateSyncResultSchema = z.object({
    source: rateSyncSourceSchema,
    applied_count: z.number().int(),
    new_countries: z.number().int(),
    rate_changes: z.number().int(),
    rates_added: z.number().int(),
    rates_removed: z.number().int(),
    effective_from: z.string(),
    jurisdictions: z.array(z.string()),
    /** Organizations whose tax rates were re-synced (sync_org_tax_rates). */
    orgs_synced: z.number().int(),
});
/** The `sync-vatstack-rates` edge function's success body. */
export const startRateSyncResultSchema = z.object({
    success: z.literal(true),
    sync_run_id: z.string(),
    source: rateSyncSourceSchema,
    triggered_by: z.string(),
    countries_checked: z.number().int(),
    proposals_created: z.number().int(),
    summary: z.record(z.string(), z.number()).optional(),
    new_countries: z.array(z.string()).optional(),
    /** Differences the sync could not turn into a proposal — for a person to check. */
    attention: z.array(z.string()).default([]),
});
//# sourceMappingURL=taxRateSync.js.map